export const extractValue = (input: any): any => {
  if (input === null || input === undefined) return undefined;
  if (typeof input === "string") return input;
  if (typeof input === "number") return input;
  if (typeof input === "boolean") return input ? "Yes" : "No";

  if (Array.isArray(input)) {
    return input
      .map((item) => extractValue(item))
      .filter((value) => value !== undefined);
  }

  if (typeof input === "object") {
    if (Object.keys(input).length === 0) return undefined;
    if ("value" in input) {
      return extractValue(input.value);
    }

    for (const key of ["name", "sector", "label", "title"]) {
      if (key in input) {
        const value = extractValue((input as Record<string, unknown>)[key]);
        if (value !== undefined) return value;
      }
    }

    return undefined;
  }

  return undefined;
};

export const toCommaList = (value: any): string => {
  const extracted = extractValue(value);
  if (extracted === undefined) return "Not available";

  if (Array.isArray(extracted)) {
    const parts = extracted
      .map((item) => {
        if (item === null || item === undefined) return undefined;
        if (typeof item === "string" || typeof item === "number")
          return String(item);
        return undefined;
      })
      .filter(Boolean) as string[];

    return parts.length ? parts.join(", ") : "Not available";
  }

  if (typeof extracted === "string" || typeof extracted === "number") {
    return String(extracted);
  }

  return "Not available";
};

export const formatLocation = (value: any): string => {
  if (!value || typeof value !== "object") return "Not available";
  const city = extractValue((value as Record<string, unknown>).city);
  const state = extractValue((value as Record<string, unknown>).state);
  const country = extractValue((value as Record<string, unknown>).country);
  const parts = [city, state, country].filter(Boolean) as Array<
    string | number
  >;
  return parts.length ? parts.join(", ") : "Not available";
};

/**
 * The amounts these format are USD (check_size_usd, aum_usd, size_usd,
 * exit_value_usd) and the "$" prefix below is hardcoded, so the grouping has to
 * be pinned to match it. Bare toLocaleString() follows the *viewer's* locale,
 * which rendered "$1 000 000" on a Swedish machine and "$1,000,000" in CI --
 * a dollar sign with European grouping, and a test suite that passed or failed
 * depending on whose laptop ran it.
 */
const CURRENCY_LOCALE = "en-US";

export const formatCurrency = (value: any): string => {
  if (value === null || value === undefined) return "Not available";
  const numericValue = typeof value === "number" ? value : Number(value);
  if (Number.isNaN(numericValue)) return "Not available";
  return `$${numericValue.toLocaleString(CURRENCY_LOCALE)}`;
};

export const formatCheckSize = (value: any): string => {
  if (!value || typeof value !== "object") return "Not available";
  const min = extractValue((value as Record<string, unknown>).min);
  const max = extractValue((value as Record<string, unknown>).max);
  const formattedMin = min !== undefined ? formatCurrency(min) : undefined;
  const formattedMax = max !== undefined ? formatCurrency(max) : undefined;

  if (!formattedMin && !formattedMax) return "Not available";
  if (!formattedMin) return `Up to ${formattedMax}`;
  if (!formattedMax) return `From ${formattedMin}`;
  return `${formattedMin} - ${formattedMax}`;
};

export const formatTotalFundSize = (funds: any): string => {
  if (!Array.isArray(funds) || funds.length === 0) return "Not available";

  let total = 0;
  let hasData = false;

  funds.forEach((fund) => {
    const data = fund?.value ?? fund;
    if (data && typeof data === "object") {
      const size = extractValue((data as Record<string, unknown>).size_usd);
      if (size !== undefined) {
        const numericSize =
          typeof size === "number"
            ? size
            : typeof size === "string" && !isNaN(Number(size))
              ? Number(size)
              : undefined;
        if (numericSize !== undefined) {
          total += numericSize;
          hasData = true;
        }
      }
    }
  });

  return hasData ? formatCurrency(total) : "Not available";
};

export const formatInternalCapabilities = (capabilities: any): string => {
  const extracted = extractValue(capabilities);
  if (!extracted || !Array.isArray(extracted) || extracted.length === 0)
    return "Not available";

  const capList = extracted
    .map((cap) => {
      if (cap === null || cap === undefined) return undefined;
      if (typeof cap === "string") return cap;
      return String(cap);
    })
    .filter(Boolean) as string[];

  return capList.length > 0 ? capList.join(", ") : "Not available";
};

export const formatTeamMembers = (team: any): string[] => {
  if (!Array.isArray(team)) return [];

  return team
    .map((member) => {
      const data = member?.value ?? member;
      if (!data || typeof data !== "object") return undefined;
      const name =
        extractValue((data as Record<string, unknown>).name) ??
        extractValue(data?.person);
      const role =
        extractValue((data as Record<string, unknown>).role) ??
        extractValue(data?.person?.role);
      if (!name && !role) return undefined;
      return role ? `${name || "Unknown"} (${role})` : String(name);
    })
    .filter(Boolean) as string[];
};

export const formatSingleDeal = (deal: any): string | undefined => {
  if (!deal || typeof deal !== "object") return undefined;
  const data = deal?.value ?? deal;
  if (!data || typeof data !== "object") return undefined;

  // Handle company reference - match VentureCapitalProfile.tsx logic
  const companyRef =
    (data as any).company?.value ??
    (data as any).company ??
    (data as any).portfolio_company?.value ??
    (data as any).portfolio_company;

  let company: string | undefined;
  if (companyRef && typeof companyRef === "object") {
    if (companyRef.reference && typeof companyRef.reference === "string") {
      // Extract company name from reference like "workspace/company/minerva_imaging"
      const refParts = companyRef.reference.split("/");
      const companyId = refParts[refParts.length - 1];
      // Convert company_id format to readable name
      company = companyId
        .split("_")
        .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    } else if (companyRef.name) {
      company = extractValue(companyRef.name);
    }
  } else if (typeof companyRef === "string") {
    company = companyRef;
  }

  const amount =
    extractValue((data as Record<string, unknown>).amount_usd) ??
    extractValue((data as Record<string, unknown>).amount);
  const stage =
    extractValue((data as Record<string, unknown>).stage) ??
    extractValue((data as Record<string, unknown>).round);
  const date = extractValue((data as Record<string, unknown>).date);

  const parts = [
    company,
    amount ? formatCurrency(amount) : undefined,
    stage,
    date ? new Date(date).toLocaleDateString() : undefined,
  ].filter(Boolean) as string[];

  return parts.length ? parts.join(" - ") : undefined;
};

export const formatDeals = (deals: any): string[] => {
  if (!Array.isArray(deals)) return [];

  return deals
    .map((deal) => formatSingleDeal(deal))
    .filter(Boolean) as string[];
};

export const formatSingleExit = (exit: any): string | undefined => {
  if (!exit || typeof exit !== "object") return undefined;
  const data = exit?.value ?? exit;
  if (!data || typeof data !== "object") return undefined;

  const companyName = extractValue(
    (data as Record<string, unknown>).company_name,
  );
  const exitType = extractValue((data as Record<string, unknown>).exit_type);
  const exitValue =
    extractValue((data as Record<string, unknown>).exit_value_usd) ??
    extractValue((data as Record<string, unknown>).exit_value);
  const date =
    extractValue((data as Record<string, unknown>).exit_date) ??
    extractValue((data as Record<string, unknown>).date);

  const parts = [
    companyName,
    exitType,
    exitValue ? formatCurrency(exitValue) : undefined,
    date ? new Date(date).toLocaleDateString() : undefined,
  ].filter(Boolean) as string[];

  return parts.length ? parts.join(" - ") : undefined;
};

export const formatExits = (exits: any): string[] => {
  if (!Array.isArray(exits)) return [];

  return exits
    .map((exit) => formatSingleExit(exit))
    .filter(Boolean) as string[];
};

export const formatFunds = (funds: any): string[] => {
  if (!Array.isArray(funds)) return [];

  return funds
    .map((fund) => {
      const data = fund?.value ?? fund;
      if (!data || typeof data !== "object") return undefined;
      const name = extractValue((data as Record<string, unknown>).name);
      const year = extractValue((data as Record<string, unknown>).vintage_year);
      const size = extractValue((data as Record<string, unknown>).size_usd);
      const parts = [
        name,
        year ? `(${year})` : undefined,
        size ? formatCurrency(size) : undefined,
      ].filter(Boolean) as string[];
      return parts.length ? parts.join(" ") : undefined;
    })
    .filter(Boolean) as string[];
};

export const ensureStringArray = (value: any): string[] => {
  if (value === null || value === undefined) return [];
  const extracted = extractValue(value);

  if (extracted === null || extracted === undefined) return [];
  if (Array.isArray(extracted)) {
    return extracted
      .map((item) => {
        if (item === null || item === undefined) return undefined;
        if (typeof item === "string" || typeof item === "number")
          return String(item);
        if (typeof item === "boolean") return item ? "Yes" : "No";
        return undefined;
      })
      .filter(Boolean) as string[];
  }

  if (typeof extracted === "string" || typeof extracted === "number") {
    return [String(extracted)];
  }

  if (typeof extracted === "boolean") {
    return [extracted ? "Yes" : "No"];
  }

  return [];
};

export const titleCaseFromKey = (key: string): string => {
  return key.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
};
